import { createModel } from '@rematch/core'
import * as firebase from 'firebase'
import * as R from 'ramda'
import { dispatch, select, store } from '../store'
import { toggleElement } from '../utils'

interface IState {
  [replyId: string]: IReply
}

async function fetchReply(replyId: string) {
  const ref = await firebase
    .firestore()
    .collection('replies')
    .doc(replyId)
    .get()
  const reply = ref.data()
  if (reply) {
    return reply
  } else {
    throw Error('Cannot fetch reply')
  }
}

export default createModel({
  state: {} as IState,
  reducers: {
    updateById: (
      state: IState,
      payload: { replyId: string; reply: IReply }
    ): IState => ({
      ...state,
      [payload.replyId]: payload.reply
    })
  },
  effects: {
    async fetchReplies(replyIds: string[]) {
      replyIds.map(replyId => {
        fetchReply(replyId)
          .then(reply => this.updateById({ replyId, reply }))
          .catch(error => {
            const errorReply: IReply = {
              ts: firebase.firestore.Timestamp.now(),
              replierId: '',
              text: '無法讀取回覆',
              reactions: {
                like: []
              },
              revisions: []
            }
            this.updateById({ replyId, errorReply })
          })
      })
    },
    async toggleReaction(payload: {
      replyId: string
      reaction: string
      lectureId: string
      chapterIdx: number
      insightId: string
      materialIdx: number
    }) {
      const {
        lectureId,
        chapterIdx,
        replyId,
        reaction,
        insightId,
        materialIdx
      } = payload
      const rootState = store.getState()
      const currentUser = firebase.auth().currentUser
      if (!currentUser) {
        throw new Error('請先登入會員')
      }
      const reply = select.replies.getById(rootState, replyId)
      if (!reply) {
        throw new Error('no reply')
      }
      const updated = {
        reactions: {
          ...reply.reactions,
          [reaction]: toggleElement(currentUser.uid, reply.reactions[reaction])
        }
      }
      firebase
        .firestore()
        .collection('replies')
        .doc(replyId)
        .update(updated)
      // active reaction
      if (R.contains(currentUser.uid, updated.reactions[reaction])) {
        const event: IReplyReactionEvent = {
          type: 'NEW_REPLY_REACTION',
          payload: {
            lectureId,
            chapterIdx,
            insightId,
            materialIdx,
            replyId,
            userId: currentUser.uid
          }
        }
        firebase
          .firestore()
          .collection('events')
          .add(event)
      }
      this.updateById({
        replyId,
        reply: {
          ...reply,
          ...updated
        }
      })
    },
    async updateReply(payload: { replyId: string; updated: Partial<IReply> }) {
      const { replyId, updated } = payload
      const reply = select.replies.getById(store.getState(), replyId) as IReply
      const updatedReply = {
        ...reply,
        ...updated
      }
      if (updatedReply.text.trim()) {
        firebase
          .firestore()
          .collection('replies')
          .doc(replyId)
          .set(updatedReply)
        this.updateById({ replyId, reply: updatedReply })
      }
    },
    async addSubReply(payload: {
      lectureId: string
      chapterIdx: number
      insightId: string
      materialIdx: number
      replyId: string
      subReply: IReply
    }) {
      const {
        insightId,
        chapterIdx,
        materialIdx,
        subReply,
        lectureId,
        replyId
      } = payload
      const rootState = store.getState()
      const lecture = select.lectures.getById(rootState, lectureId)
      const insight = select.insights.getById(rootState, insightId)
      if (lecture === null || insight === null) {
        throw Error('no current lecture or insight')
      }
      if (!subReply.text.trim()) {
        throw Error('請輸入留言')
      }
      // add reply into firestore
      const replyRef = await firebase
        .firestore()
        .collection('replies')
        .doc(replyId)
        .collection('subreplies')
        .add(subReply)
      const replySnap = await replyRef.get()
      const subReplyId = replySnap.id
      dispatch.replies.fetchReplies([subReplyId])
      const event: IReplyEvent = {
        type: 'NEW_REPLY',
        payload: {
          isSpeaker: R.contains(subReply.replierId, lecture.speakerIds),
          lectureId,
          chapterIdx,
          insightId,
          materialIdx,
          replyId,
          reply: subReply
        }
      }
      firebase
        .firestore()
        .collection('events')
        .add(event)
      this.updateById({ replyId: subReplyId, reply: subReply })
    }
  },
  selectors: {
    getById: (state: IState, replyId: string) => {
      return state[replyId] ? state[replyId] : null
    }
  }
})



// WEBPACK FOOTER //
// ./src/models/replies.ts